import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  LinearProgress,
  Avatar,
  Tooltip,
  Stack,
  Alert
} from '@mui/material';
import {
  People,
  Settings,
  TrendingUp,
  Assignment,
  Edit,
  Balance,
  Schedule,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import api from '../services/api';

interface AgentWorkload {
  user_id: number;
  user_name: string;
  user_email: string;
  active_conversations: number;
  pending_conversations: number;
  resolved_today: number;
  conversations_by_channel: Record<string, number>;
  avg_response_time_minutes: number;
  load_score: number;
  status: string;
  is_available: boolean;
  max_conversations: number;
}

interface AgentCapacity {
  id: number;
  user_id: number;
  max_conversations: number;
  max_conversations_per_channel: Record<string, number>;
  auto_assignment_enabled: boolean;
  auto_assignment_priority: number;
  working_hours_enabled: boolean;
  working_hours: Record<string, { start: string; end: string }>;
  timezone: string;
  is_available: boolean;
  status: string;
  status_message?: string;
  current_workload?: {
    active_conversations: number;
    pending_conversations: number;
    resolved_today: number;
    load_score: number;
  };
}

interface WorkloadSummary {
  total_agents: number;
  available_agents: number;
  total_capacity: number;
  total_active_conversations: number;
  utilization_rate: number;
  average_load_per_agent: number;
}

const AgentCapacityPage: React.FC = () => {
  const [workloads, setWorkloads] = useState<AgentWorkload[]>([]);
  const [summary, setSummary] = useState<WorkloadSummary | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<AgentCapacity | null>(null);
  const [isCapacityDialogOpen, setIsCapacityDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch workloads
      const workloadResponse = await api.get('/agent-capacity/');
      setWorkloads(workloadResponse.data.workloads || []);

      // Fetch summary
      const summaryResponse = await api.get('/agent-capacity/workload/summary');
      setSummary(summaryResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCapacity = async (userId: number) => {
    try {
      const response = await api.get(`/agent-capacity/${userId}`);
      setSelectedAgent(response.data);
      setIsCapacityDialogOpen(true);
    } catch (error) {
      console.error('Error fetching agent capacity:', error);
    }
  };

  const handleUpdateCapacity = async () => {
    if (!selectedAgent) return;

    try {
      await api.put(`/agent-capacity/${selectedAgent.user_id}`, {
        max_conversations: selectedAgent.max_conversations,
        max_conversations_per_channel: selectedAgent.max_conversations_per_channel,
        auto_assignment_enabled: selectedAgent.auto_assignment_enabled,
        auto_assignment_priority: selectedAgent.auto_assignment_priority,
        working_hours_enabled: selectedAgent.working_hours_enabled,
        working_hours: selectedAgent.working_hours,
        timezone: selectedAgent.timezone
      });

      setIsCapacityDialogOpen(false);
      setSelectedAgent(null);
      fetchData();
    } catch (error) {
      console.error('Error updating capacity:', error);
    }
  };

  const handleStatusUpdate = async (userId: number, status: string) => {
    try {
      await api.put(`/agent-capacity/${userId}/status`, { status });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status: string, loadScore: number) => {
    if (status === 'offline') return 'error';
    if (status === 'away') return 'warning';
    if (loadScore >= 90) return 'error';
    if (loadScore >= 70) return 'warning';
    return 'success';
  };

  const getStatusText = (status: string, loadScore: number) => {
    if (status === 'offline') return 'Offline';
    if (status === 'away') return 'Away';
    if (status === 'busy') return 'Busy';
    if (loadScore >= 90) return 'Overloaded';
    if (loadScore >= 70) return 'High Load';
    return 'Available';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Agent Capacity Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Balance />}
          onClick={() => {
            // Trigger workload rebalancing
            console.log('Rebalance workload');
          }}
        >
          Rebalance Workload
        </Button>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Agents
                    </Typography>
                    <Typography variant="h4" component="div">
                      {summary.total_agents}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 40, color: '#1f93ff' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Available Agents
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ color: '#4caf50' }}>
                      {summary.available_agents}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 40, color: '#4caf50' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Capacity
                    </Typography>
                    <Typography variant="h4" component="div">
                      {summary.total_capacity}
                    </Typography>
                  </Box>
                  <Assignment sx={{ fontSize: 40, color: '#ff9800' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Utilization Rate
                    </Typography>
                    <Typography variant="h4" component="div" 
                      sx={{ color: summary.utilization_rate > 80 ? '#f44336' : '#4caf50' }}>
                      {summary.utilization_rate}%
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: summary.utilization_rate > 80 ? '#f44336' : '#4caf50' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Workload Table */}
      <Card>
        <CardHeader
          title="Agent Workload Overview"
          action={
            <Button onClick={fetchData} startIcon={<Settings />}>
              Refresh
            </Button>
          }
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Active Conversations</TableCell>
                  <TableCell align="center">Load</TableCell>
                  <TableCell align="center">Capacity</TableCell>
                  <TableCell align="center">Resolved Today</TableCell>
                  <TableCell align="center">Avg Response</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workloads.map((workload) => (
                  <TableRow key={workload.user_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: '#1f93ff' }}>
                          {workload.user_name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {workload.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {workload.user_email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={getStatusText(workload.status, workload.load_score)}
                        color={getStatusColor(workload.status, workload.load_score)}
                        variant={workload.is_available ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {workload.active_conversations}
                        {workload.pending_conversations > 0 && (
                          <Chip 
                            size="small" 
                            label={`+${workload.pending_conversations} pending`}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: 60, mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(workload.load_score, 100)}
                            color={workload.load_score >= 90 ? 'error' : workload.load_score >= 70 ? 'warning' : 'success'}
                          />
                        </Box>
                        <Typography variant="body2">
                          {workload.load_score}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {workload.max_conversations}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {workload.resolved_today}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {workload.avg_response_time_minutes}m
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Edit Capacity">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditCapacity(workload.user_id)}
                          >
                            <Settings />
                          </IconButton>
                        </Tooltip>
                        <Select
                          size="small"
                          value={workload.status}
                          onChange={(e) => handleStatusUpdate(workload.user_id, e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="available">Available</MenuItem>
                          <MenuItem value="busy">Busy</MenuItem>
                          <MenuItem value="away">Away</MenuItem>
                          <MenuItem value="offline">Offline</MenuItem>
                        </Select>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Capacity Settings Dialog */}
      <Dialog open={isCapacityDialogOpen} onClose={() => setIsCapacityDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Agent Capacity Settings</DialogTitle>
        <DialogContent>
          {selectedAgent && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Max Conversations"
                    type="number"
                    value={selectedAgent.max_conversations}
                    onChange={(e) => setSelectedAgent({
                      ...selectedAgent,
                      max_conversations: parseInt(e.target.value) || 0
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Assignment Priority"
                    type="number"
                    value={selectedAgent.auto_assignment_priority}
                    onChange={(e) => setSelectedAgent({
                      ...selectedAgent,
                      auto_assignment_priority: parseInt(e.target.value) || 1
                    })}
                    helperText="Lower number = higher priority"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedAgent.auto_assignment_enabled}
                        onChange={(e) => setSelectedAgent({
                          ...selectedAgent,
                          auto_assignment_enabled: e.target.checked
                        })}
                      />
                    }
                    label="Enable Auto Assignment"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedAgent.working_hours_enabled}
                        onChange={(e) => setSelectedAgent({
                          ...selectedAgent,
                          working_hours_enabled: e.target.checked
                        })}
                      />
                    }
                    label="Enable Working Hours"
                  />
                </Grid>
                
                {selectedAgent.current_workload && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2">Current Workload</Typography>
                      <Typography variant="body2">
                        Active: {selectedAgent.current_workload.active_conversations} | 
                        Pending: {selectedAgent.current_workload.pending_conversations} | 
                        Resolved Today: {selectedAgent.current_workload.resolved_today} | 
                        Load Score: {selectedAgent.current_workload.load_score}%
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCapacityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCapacity} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentCapacityPage;